import { AxiosError } from "axios";
import api from "../api";
import { Driver } from '../types/driver.types';

export class DriverService {
  async getDrivers(transporterId: string): Promise<Driver[]> {
    try {
      const response = await api.get(`/drivers/transporters/${transporterId}`);
      return response.data.data;
    } catch (error) {
      this.handleError(error as AxiosError, 'Error fetching drivers');
    }
  }

  async getDriver(driverId: string): Promise<Driver> {
    try {
      const response = await api.get(`/drivers/${driverId}`);
      return response.data.data;
    } catch (error) {
      this.handleError(error as AxiosError, 'Error fetching driver');
    }
  }

  async addDriver(transporterId: string, driverData: Omit<Driver, '_id' | 'password'>): Promise<Driver> {
    try {
      const formattedDriverData = this.formatDates(driverData);
      const response = await api.post(`/drivers/${transporterId}`, formattedDriverData);
      return response.data.data;
    } catch (error) {
      this.handleError(error as AxiosError, 'Error adding driver');
    }
  }

  async updateDriver(driverId: string, driverData: Partial<Driver>): Promise<Driver> {
    try {
      const formattedDriverData = this.formatDates(driverData);
      const response = await api.put(`/drivers/${driverId}`, formattedDriverData);
      return response.data.data;
    } catch (error) {
      this.handleError(error as AxiosError, 'Error updating driver');
    }
  }

  async deleteDriver(driverId: string, transporterId: string): Promise<void> {
    try {
      await api.delete(`/drivers/${driverId}/transporters/${transporterId}`);
    } catch (error) {
      this.handleError(error as AxiosError, 'Error deleting driver');
    }
  }

  async resetDriverPassword(driverId: string): Promise<void> {
    try {
      await api.post(`/drivers/${driverId}/reset-password`);
    } catch (error) {
      this.handleError(error as AxiosError, 'Error resetting driver password');
    }
  }

  async getRemainingDriverSlots(transporterId: string): Promise<number> {
    try {
      const response = await api.get(`/transporters/${transporterId}/remaining-driver-slots`);
      return response.data.data;
    } catch (error) {
      this.handleError(error as AxiosError, 'Error fetching remaining driver slots');
    }
  }

  private handleError(error: AxiosError, defaultMessage: string): never {
    console.error(defaultMessage, error);
    let errorMessage = defaultMessage;

    if (error.response) {
      console.error('API Error:', error.response);

      // Extract the error message from the API response
      if (error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = error.response.data.message as string;
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      errorMessage = 'No response received from the server';
    } else {
      console.error('Error setting up request:', error.message);
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }

  private formatDates<T extends object>(data: T): T {
    const formatted = { ...data };
    for (const [key, value] of Object.entries(formatted)) {
      if (value instanceof Date) {
        (formatted as any)[key] = value.toISOString();
      }
    }
    return formatted;
  }
}
